import React, {Suspense, useCallback, useEffect, useId, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {treeRelativePath} from "seo/documentLang";
import {BLOG_INDEX_PATH} from "seo/blogSite";
import {SiteLink as RouterLink} from "nav/SiteLink";
import {useHistory} from "utils/HistoryUtils";
import {useLoginModal} from "login/LoginModalProvider";
import {focusOpener} from "components/sheetOpener";
import {useLocale, useMessages} from "i18n";
// The EAGER four-field content index (ui#1004). Already in this chunk through `routes.tsx`, and it
// carries no page bodies — only key, locale, path and title — so reading it here adds no weight.
import {CONTENT_PAGES} from "virtual:content-index";
import Feedback from "feedback/Feedback";
import {FeedbackEdgeTab} from "feedback/FeedbackEdgeTab";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import EnhancedEncryptionOutlinedIcon from "@mui/icons-material/EnhancedEncryptionOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import NewspaperOutlinedIcon from "@mui/icons-material/NewspaperOutlined";
import LanguageSubmenuItem from "nav/LanguageSubmenu";
import {DropdownRoot, Panel, PanelItem, PanelLayer, useHoverMenu} from "nav/hoverMenu";
import {LogoMark} from "nav/BrandMark";
import {useIsLoggedIn, useUser} from "UserProvider";
import {useAppNavigation} from "nav/useAppNavigation";
import {isCurrentDestination} from "nav/appDestinations";
import {preloadRoute} from "nav/preloadRoutes";
import {NAV_PILL_BAR_ATTR} from "nav/navPillBar";
import ProfileMenu from "nav/pro/ProfileMenu";
import DarkThemeToggle from "nav/pro/DarkThemeToggle";
import {AccountIcon, BrandingIcon, DESTINATION_ICONS} from "nav/pro/icons";
import SupportRequestDialog from "support/SupportRequestDialog";
import {useBillingSheetRequests} from "account/billing/useBillingSheetRequest";
import {supportFramingForVisitor} from "support/supportEntitlement";
import {HowItWorksDialog} from "home/landing/HowItWorksDialog";
import {PricingDialog} from "pricing/PricingDialog";
import {usePricingUpgrade} from "pricing/usePricingUpgrade";
import {CompactUpgradeDialog} from "upsell/CompactUpgradeDialog";
import {PlanChangeDialog} from "pricing/PlanChangeDialog";
import {PolicyDialog} from "legal/PolicyDialog";
import {BrandSheet} from "account/brand/BrandSheet";
import {
	isPersonalizationRoute,
	PERSONALIZATION_ROUTE,
	personalizationCloseAction,
} from "account/brand/personalizationRoute";
import {CookieSettingsDialog} from "legal/CookieSettingsDialog";
import {useHasMounted} from "utils/useHasMounted";
import {ResourceDialog} from "nav/ResourceDialog";
import {GuidesDialog} from "nav/GuidesDialog";
import {guideNavEntryFor} from "content/guideCatalogue";
import {
	ADVERTISING_RESOURCE_LINKS,
	EXPLORE_RESOURCE_LINKS,
	isPlainPrimaryClick,
	LEGAL_RESOURCE_LINKS,
	type ResourceDialogTarget,
	type ResourceKind,
} from "nav/resourceNavigation";
import {collapsibleNavItem} from "nav/navCollapse";
import {navigateSiteLink} from "nav/siteLinkOrigin";
import {useSiteLinkOrigin} from "nav/useSiteLinkOrigin";
import {
	Brand,
	DestinationLink,
	LoginButton,
	LoginGlyph,
	LoginLabel,
	MarketingAnchor,
	NavLinks,
	NavSeparator,
	PillButton,
	PillIcon,
	PillLabel,
	SheetPillButton,
	ToggleSlot,
	Topbar,
	Wordmark,
} from "./NavBar.styles";
import styles from "./NavBar.module.css";

/**
 * v3 top navigation (design `.topbar`, single-screen model since ticket #45): logo mark +
 * "Transferify" wordmark on the left, a pill of links on the right — floating over the page backdrop
 * with NO bar surface of its own, ever (the design topbar is `position:absolute`, background-less).
 *
 * The SAME bar serves both auth states (ui#96), chosen hydration-safely via {@link useIsLoggedIn}:
 * - Logged OUT (also the SSG/first-render state): the public pill — Home / Features / Pricing /
 *   Resources, then Log in after a separator. Home is a plain route link and has been a permanent
 *   pill item since ui#780, which took it out of the Resources menu; Features and Pricing are
 *   crawlable route links whose plain primary activation opens the existing in-place dialogs;
 *   Resources holds the remaining public routes in mounted SSG markup; Log in opens the existing
 *   {@link useLoginModal} experience directly.
 * - Logged IN: the app pill — the primary destinations (Home / Transfers / Contacts / Reminders)
 *   from {@link useAppNavigation}, then Personalization (ui#450) and Resources, then the
 *   {@link DarkThemeToggle} and the {@link ProfileMenu} avatar, which is also where Account lives.
 *   Resources opens dialogs rather than routing; Personalization ROUTES since ui#1292 — it is a
 *   destination of its own now, opened and closed by a toggle button rather than a link. Every pill
 *   item is text on desktop and icon-only ≤640px.
 *
 * Resources holds the app's SINGLE Support entry since #520 — the ProfileMenu's second one is gone.
 * The row is auth-aware: anonymous visitors get the public feedback form exactly as before, signed-in
 * users get the authenticated request flow with its entitlement-driven priority framing.
 *
 * Icon-only is a size reduction, not an overflow strategy: the row still had none, so the pill grew
 * past the viewport edge at every width below ~807px and clipped controls with no gesture to reveal
 * them (ui#451). The bar now COLLAPSES instead — at the measured widths in {@link navCollapse} the
 * lower-traffic items render as {@link ProfileMenu} entries rather than pill items, the way ui#117
 * moved Language there. One table drives both halves of the swap, so an item is in exactly one of the
 * two places at any width.
 *
 * Personalization collapses over a BAND rather than everything below a ceiling (ui#452): 641–879px,
 * where it would have to render a text label it has no room for. On a phone (≤640px) it comes back
 * into the pill as the same 44px icon square every other item becomes there, so the sender reaches it
 * with one tap instead of two through the popover — the owner's 2026-08-08 call.
 *
 * Light/dark come from `theme.tokens`; geometry tightens on mobile per the design's `.mobile`
 * rules. SSG-safe: no window/document access at module scope or during render — the collapse is pure
 * CSS media queries, so it needs no `matchMedia` and cannot mismatch the pre-rendered HTML.
 *
 * `forwardRef` is required because MUI transitions (historically a `<Fade>` wrapper) read their
 * child's DOM node; a transition child that doesn't forward a ref crashes under React 19 + MUI 9.
 */

/** Distinct decorative glyphs from the approved Resources design handoff. */
const RESOURCE_LINK_ICONS = {
	about: <InfoOutlinedIcon aria-hidden="true"/>,
	p2p: <HubOutlinedIcon aria-hidden="true"/>,
	clientServer: <DnsOutlinedIcon aria-hidden="true"/>,
	zeroKnowledge: <EnhancedEncryptionOutlinedIcon aria-hidden="true"/>,
	contact: <ContactMailOutlinedIcon aria-hidden="true"/>,
	advertise: <CampaignOutlinedIcon aria-hidden="true"/>,
	cancelContract: <CancelOutlinedIcon aria-hidden="true"/>,
	terms: <GavelOutlinedIcon aria-hidden="true"/>,
	advertisingTerms: <ReceiptLongOutlinedIcon aria-hidden="true"/>,
	privacy: <PrivacyTipOutlinedIcon aria-hidden="true"/>,
	impressum: <BusinessOutlinedIcon aria-hidden="true"/>,
} satisfies Record<
	(typeof EXPLORE_RESOURCE_LINKS | typeof ADVERTISING_RESOURCE_LINKS | typeof LEGAL_RESOURCE_LINKS)[number]["labelKey"],
	React.ReactNode
>;

type LegalDialog = "cookie" | "cookieSettings" | null;
type NavSheet = "features" | "pricing" | null;

/**
 * The Analytics sheet is a LAZY chunk, and that is not an optimization — it is what keeps this bar
 * buildable (ui#1522).
 *
 * It pulls in `@superset-ui/embedded-sdk`, which is CommonJS. Imported eagerly it joins the graph of
 * every PRE-RENDERED public page, and the SSG prerender dies evaluating it in Node with
 * `SyntaxError: Named export 'embedDashboard' not found`. It also has no business in the entry bundle:
 * `routes.tsx` keeps the private routes lazy precisely so the heavy authenticated code never weighs
 * down a first paint, and this bar renders on every public page.
 *
 * Fetched on the FIRST open only, then kept mounted so the exit transition can finish — the same
 * shape {@link ../../commands/useCommandPalette} uses for the palette. The named export is remapped
 * to `default` exactly as the lazy routes do.
 */
const AnalyticsSheet = React.lazy(() =>
	import("account/analytics/AnalyticsSheet").then(m => ({default: m.AnalyticsSheet})));

/**
 * The Billing sheet is a LAZY chunk too (ui#1791), for the second of the two reasons above rather than
 * the first: it drags the whole account billing surface — the subscription card, the referral card and
 * the upgrade checkout dialog they raise — and this bar renders on every PUBLIC page, where none of that
 * is reachable. Fetched on the first open only, then kept mounted so the exit transition can finish.
 */
const BillingSheet = React.lazy(() =>
	import("account/billing/BillingSheet").then(m => ({default: m.BillingSheet})));

/**
 * Shared shell for the pill glyphs (#106), drawn to the same conventions as the rail/destination
 * icons (24-unit viewBox, 2px rounded strokes, `currentColor`) so the icon-only public pill matches
 * the logged-in one on a phone. Used by both bars' non-destination items.
 */
function PillGlyph({children}: {children: React.ReactNode}) {
	return (
		<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
			 strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			{children}
		</svg>
	);
}

/** Features — a sparkle/shine star (product highlights). */
function FeaturesIcon() {
	return (
		<PillGlyph>
			<path d="M12 3c.5 4.2 1.8 5.5 6 6-4.2.5-5.5 1.8-6 6-.5-4.2-1.8-5.5-6-6 4.2-.5 5.5-1.8 6-6z"/>
		</PillGlyph>
	);
}

/** Pricing — a price tag with its eyelet hole. */
function PricingIcon() {
	return (
		<PillGlyph>
			<path d="M4 4h7l9 9-7 7-9-9V4z"/>
			<circle cx="8" cy="8" r="1.3"/>
		</PillGlyph>
	);
}

/** Resources — an open book (guides & policies). */
function ResourcesIcon() {
	return (
		<PillGlyph>
			<path d="M12 6a4 4 0 0 0-4-2H4v13h4a4 4 0 0 1 4 2z"/>
			<path d="M12 6a4 4 0 0 1 4-2h4v13h-4a4 4 0 0 0-4 2z"/>
		</PillGlyph>
	);
}

/**
 * The size the {@link PillGlyph} shell draws at, applied to the shared {@link BrandingIcon} so the
 * Branding pill keeps the exact glyph metrics the ui#451 collapse thresholds were measured against.
 */
const PILL_GLYPH_SIZE = 18;

/**
 * The `appDestinations` rows the pill's own map must NOT render, because this file renders them itself
 * or does not render them at all.
 *
 * A set rather than two inline comparisons so the two exclusions are one stated list, and so a third
 * cannot be added by tacking another `&&` onto a filter nobody reads:
 *  - `/account` is reachable from the {@link ProfileMenu} and has never been a pill item;
 *  - `/personalization` IS a pill item, but a toggling {@link SheetPillButton} rather than a
 *    {@link DestinationLink} (ui#1243, kept through ui#1292's move to a route) — mapping it here would
 *    render it a second time, as a link that cannot close what it opened.
 */
const PILL_RENDERED_ELSEWHERE: ReadonlySet<string> = new Set(["/account", PERSONALIZATION_ROUTE]);

const NavBar = React.forwardRef<HTMLElement, React.ComponentPropsWithoutRef<"header">>(
	function NavBar(props, ref) {
		const history = useHistory();
		const location = useLocation();
		const loginModal = useLoginModal();
		const {nav, brand} = useMessages();
		// The READING locale, the same one `useMessages` just resolved: the row labels and the pages they
		// point at have to be in one language. It equals the document's published locale on the first
		// render, so the pre-rendered panel and its hydration agree.
		const {locale} = useLocale();
		// The Guides row and everything its dialog offers, resolved from the corpus this locale actually
		// ships (ui#1555). `null` — and so no row at all — for a locale with no guides overview page.
		const guideNav = guideNavEntryFor(CONTENT_PAGES, locale);
		// Its only door is the pricing dialog below, so every checkout here is the plan picker's.
		const {onUpgrade, checkout, closeCheckout, planChange} = usePricingUpgrade("PLAN_UPGRADE");
		const hasMounted = useHasMounted();
		// ui#432: on a claimed subdomain (and any other non-canonical host) the edge serves recipient
		// surfaces ONLY, so every root-relative nav link below is a 404 there. This resolves the brand
		// origin those links must be absolutised against — and is `null`, leaving them byte-identical,
		// on every brand host and in local development.
		const siteOrigin = useSiteLinkOrigin();
		// Hydration-safe (ui#74): false on the server AND first client render, so the SSG-pre-rendered
		// public bar hydrates deterministically and the app pill swaps in only after mount.
		const loggedIn = useIsLoggedIn();
		// Read for the Support row's entitlement label only, and always through `loggedIn` — the plan of
		// a session the first render does not yet admit to must not change pre-rendered copy.
		const [user] = useUser();
		const {destinations} = useAppNavigation();
		const [sheet, setSheet] = useState<NavSheet>(null);
		const [featuresFocus, setFeaturesFocus] = useState<"faq" | null>(null);
		const sheetOpenerRef = useRef<HTMLElement | null>(null);
		// Which cookie dialog (if any) the Resources dropdown opened.
		const [legal, setLegal] = useState<LegalDialog>(null);
		// The top bar's Personalization card sheet (ui#450, re-framed in ui#457, moved onto the shared
		// non-modal card with every other sheet). It has TWO openers since ui#451: the pill, and — in the
		// 641–879px band where the pill collapses (ui#452) — a ProfileMenu row that is gone by the time
		// the sheet closes. Neither the sheet nor an overlay primitive can tell which one raised it, so
		// the opener is captured here and handed to the sheet as its explicit return target; the sheet
		// then restores through `components/sheetOpener`, which refuses a detached or hidden node.
		//
		// Its OPEN STATE is the ROUTE since ui#1292, not the local boolean this used to hold. ui#450 chose
		// "a sheet at no URL" deliberately; the owner reversed that on 2026-08-19 because of a consequence
		// it did not anticipate. The shared card band allows one sheet at a time, so raising this one from
		// `/contacts` EVICTED the destination sheet — and `FeaturePanel`'s exit handler then ran
		// `navigate(PANEL_CLOSE_TARGET, {replace: true})`, erasing the sender's route with no history entry
		// left for Back. Deriving `open` from the route is what removes the eviction entirely: on this path
		// the App shell mounts no FeaturePanel at all, so there is nothing to evict and nothing to replace.
		// `loggedIn` is part of the condition because the bar is hydration-safe (false on the server and
		// the first client render) and because a signed-out deep link is redirected by `PrivateRoute` — the
		// sheet must not flash on its way there. See `account/brand/personalizationRoute` for the rules.
		const brandOpen = loggedIn && isPersonalizationRoute(location.pathname);
		const brandOpenerRef = useRef<HTMLElement | null>(null);
		// The focus target of LAST resort, for the opens nothing on this bar initiated: the command palette
		// pushes `/personalization` through the shared destinations contract, and so does a deep link or a
		// typed URL. Handing the sheet `null` there sends focus nowhere on close — the `<body>` failure the
		// ProfileMenu handoff exists to avoid, arriving through a different door.
		//
		// The AVATAR rather than the Personalization pill, which is the obvious choice and the wrong one:
		// between 641 and 879px the ui#452 collapse hides that pill with `display: none` while leaving it
		// mounted, and a hidden element accepts `.focus()` by doing nothing at all. The avatar is the one
		// control the bar keeps mounted AND visible at every width, which is the same property that already
		// makes it the collapsed branding row's return target.
		const profileTriggerRef = useRef<HTMLButtonElement | null>(null);
		const resolveProfileTrigger = useCallback(() => profileTriggerRef.current, []);
		// Whether this session pushed the sheet from another app route. The browser retains the actual
		// location entry — including query and hash — so React records only whether one exists to pop.
		const personalizationHasOpeningEntryRef = useRef(false);
		// `goBack` is asynchronous. Suppress a second close before its popstate arrives, or two rapid
		// dismissals would both traverse history while the URL still reads `/personalization`.
		const personalizationClosePendingRef = useRef(false);
		/**
		 * Armed while {@link closeBrandSheet} is undoing the sheet's OWN history entry (ui#2230).
		 *
		 * That pop is not the sender navigating away from an in-place document — it returns them to the
		 * route they were already on, and it is triggered by the very click that may have just opened one.
		 * A delivered dismissing click on a Resources row (ui#1278) opens the About dialog and, roughly a
		 * frame later, pops `/personalization`; the route-invalidation effect below then tore that dialog
		 * down about 200ms after it appeared, so the entry the owner asked to DELIVER flashed and vanished
		 * — the "menu entry that visibly does nothing" ui#1278 exists to stop. `card-sheet-dismiss.spec.ts`
		 * only ever passed because Playwright happened to poll inside that flash, which is what made it
		 * fail about one run in a thousand under load.
		 *
		 * Cleared by the first route change the effect sees, so it can only ever excuse the one transition
		 * it armed, and it is armed only where a history mutation is actually about to be made.
		 */
		const brandSheetDismissalRef = useRef(false);
		const previousPathnameRef = useRef<string | null>(null);
		// Capture on ENTERING the route rather than at the pill's click. The command palette and ProfileMenu
		// can enter without that handler, while a `null` previous path identifies a deep link or refresh.
		useEffect(() => {
			const previousPathname = previousPathnameRef.current;
			previousPathnameRef.current = location.pathname;
			if (!isPersonalizationRoute(location.pathname)) {
				personalizationHasOpeningEntryRef.current = false;
				personalizationClosePendingRef.current = false;
				return;
			}
			if (previousPathname !== null && !isPersonalizationRoute(previousPathname)) {
				personalizationHasOpeningEntryRef.current = true;
				personalizationClosePendingRef.current = false;
			}
		}, [location.pathname]);
		const openBrandSheet = (opener: HTMLElement | null) => {
			brandOpenerRef.current = opener;
			// A PUSH leaves the sender's current route immediately behind the sheet. Close pops that one
			// entry; replacing it with the opener would duplicate the route and consume the next Back.
			// Which route opened the sheet is not captured here — see the effect above for why every opener
			// must be derived from the route transition instead.
			history.push(PERSONALIZATION_ROUTE);
		};
		// The ONE close path, shared by the sheet's own ✕/Escape, outside-click dismissal (ui#1247) and the
		// pill's toggle (ui#1243), so they cannot drift into different history mutations. It runs after the
		// triggering click finishes: a delivered nav link can replace the sheet entry with its destination
		// first, while a swallowed click, Escape or ✕ still leaves this route current and therefore pops it.
		// Reading `window.location` in the next task observes the navigation that actually won the gesture
		// instead of the React render whose closure received the click. A microtask is too early here:
		// Chromium runs it after the document-capture listener, before React's delegated link handler.
		// The captured opener remains available until `onBrandSheetExited` releases it after focus restoration.
		const closeBrandSheet = () => {
			if (personalizationClosePendingRef.current) {
				return;
			}
			personalizationClosePendingRef.current = true;
			window.setTimeout(() => {
				const locationAfterGesture = `${window.location.pathname}${window.location.search}${window.location.hash}`;
				const action = personalizationCloseAction(
					locationAfterGesture,
					personalizationHasOpeningEntryRef.current,
				);
				if (action === null) {
					personalizationClosePendingRef.current = false;
					return;
				}
				// The route change about to happen is this sheet undoing its OWN entry, not the sender
				// steering somewhere new — see `brandSheetDismissalRef`.
				brandSheetDismissalRef.current = true;
				if (action.kind === "back") {
					history.goBack();
					return;
				}
				history.replace(action.target);
			});
		};
		// Cleared only once the exit transition is over: the sheet reads this element as it restores, so
		// dropping it any earlier would leave the restore with nothing to aim at.
		const onBrandSheetExited = () => {
			brandOpenerRef.current = null;
		};
		/**
		 * The Analytics sheet (ui#1522) — the account's embedded dashboards, moved off the account page's
		 * tab row into a sheet of their own.
		 *
		 * A plain boolean, NOT a route, and that is the deliberate difference from `brandOpen` above.
		 * ui#1292 gave personalization a URL because the command palette, a deep link and a typed address
		 * all have to be able to open it; nothing enters this sheet except the dropdown row, and the
		 * analytics tab it replaces had no URL either, so there is no deep link to preserve. Keeping it off
		 * the router is also what keeps `SPA_PREFIXES` in kitecorp/devops untouched: a new client-only
		 * segment would 404 at the edge until that hand-mirrored list was updated and applied.
		 *
		 * It IS a {@link CardSheet} since ui#1531 (owner), so it shares the single sheet band with the
		 * destination panels and the branding sheet: opening it from `/contacts` closes the panel
		 * underneath rather than covering it. That is the primitive's one-card rule, not a local decision
		 * — see {@link AnalyticsSheet} for why the owner took that trade.
		 *
		 * Its opener is a ProfileMenu row that unmounts with the popover, so the durable avatar trigger is
		 * captured here and handed to the sheet — the same handoff the branding row uses.
		 */
		const [analyticsOpen, setAnalyticsOpen] = useState(false);
		// Mounted separately from open, and it never goes back to false: the sheet's chunk is fetched on
		// the first pick, and a closed sheet has to stay mounted for its own exit transition to run.
		const [analyticsMounted, setAnalyticsMounted] = useState(false);
		const analyticsOpenerRef = useRef<HTMLElement | null>(null);
		const openAnalyticsSheet = (opener: HTMLElement | null) => {
			analyticsOpenerRef.current = opener;
			setAnalyticsMounted(true);
			setAnalyticsOpen(true);
		};
		const closeAnalyticsSheet = () => setAnalyticsOpen(false);
		// Cleared only once the exit transition is over, exactly as `onBrandSheetExited` is: the card reads
		// this element as it restores focus, so dropping it any earlier leaves the restore nothing to aim
		// at. The restore itself is the primitive's now — it goes through the same `components/sheetOpener`
		// this used to call directly, which refuses a detached or overlay-hidden target rather than
		// dropping focus to `<body>`.
		const onAnalyticsSheetExited = () => {
			analyticsOpenerRef.current = null;
		};
		/**
		 * The Billing sheet (ui#1791) — the account's plan, payments and referral card, moved off the
		 * account page's tab row into a sheet of their own. Everything the analytics sheet above says about
		 * being a boolean rather than a route applies here word for word: the billing TAB had no URL
		 * either, so there is no deep link to preserve and `SPA_PREFIXES` in kitecorp/devops stays as it is.
		 *
		 * Its state is separate from analytics rather than one "which sheet" value, because the two are
		 * mounted independently: each keeps its own lazy chunk alive for its own exit transition, and the
		 * shared band already guarantees only one of them is OPEN at a time.
		 */
		const [billingOpen, setBillingOpen] = useState(false);
		const [billingMounted, setBillingMounted] = useState(false);
		const billingOpenerRef = useRef<HTMLElement | null>(null);
		const openBillingSheet = (opener: HTMLElement | null) => {
			billingOpenerRef.current = opener;
			setBillingMounted(true);
			setBillingOpen(true);
		};
		const closeBillingSheet = () => setBillingOpen(false);
		// Cleared only once the exit transition is over, exactly as the two above are: the card reads this
		// element as it restores focus, so dropping it any earlier leaves the restore nothing to aim at.
		const onBillingSheetExited = () => {
			billingOpenerRef.current = null;
		};
		/**
		 * The sheet's SECOND door (kitecorp/transferify-ui#3004): the create panel's unused-purchase note
		 * says "Details and refunds in Billing", and that panel cannot reach this bar's state, so it asks
		 * through the React-free request store and this effect answers. Focus returns to the avatar, the
		 * one control the bar keeps mounted at every width, exactly as it does when the profile-menu row
		 * that raised the sheet has unmounted. Only a count that MOVED after this bar mounted opens the
		 * sheet: the ref is seeded with the count at mount, so neither a page load nor a remount of the
		 * bar over an already-answered request opens anything.
		 */
		const billingRequests = useBillingSheetRequests();
		const answeredBillingRequests = useRef(billingRequests);
		useEffect(() => {
			if (billingRequests === answeredBillingRequests.current) {
				return;
			}
			answeredBillingRequests.current = billingRequests;
			openBillingSheet(resolveProfileTrigger());
			// Keyed on the request count alone: `openBillingSheet` is a stable closure over setters, and
			// re-running for a new identity of it would reopen a sheet the reader just closed.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [billingRequests]);
		const [resource, setResource] = useState<ResourceKind | null>(null);
		// The Guides sheet (ui#1555). A plain boolean rather than a route, like the analytics sheet above:
		const [guidesOpen, setGuidesOpen] = useState(false);
		const displayedResourceRef = useRef<ResourceKind>("about");
		const pendingResourceTargetRef = useRef<ResourceDialogTarget | null>(null);
		const resourcesPanelId = useId();
		const exploreGroupLabelId = useId();
		const advertisingGroupLabelId = useId();
		const supportLegalGroupLabelId = useId();

		// Resources is a NON-modal hover dropdown (ui#111): the shared {@link useHoverMenu} primitive
		// gives it hover-intent open/close, click-toggle, Escape (restores focus) and outside-click —
		// all with NO full-screen backdrop, so the old MUI-Menu hover flicker cannot happen.
		const resources = useHoverMenu();
		// A route mutation invalidates an in-place document: direct navigation owns the surface again.
		// Except the Personalization sheet's own dismissal, which only undoes the entry that sheet
		// pushed — see `brandSheetDismissalRef` for the dialog that gesture opens and must not lose.
		useEffect(() => {
			if (brandSheetDismissalRef.current) {
				brandSheetDismissalRef.current = false;
				return;
			}
			pendingResourceTargetRef.current = null;
			setResource(null);
			setGuidesOpen(false);
		}, [location.pathname, location.search, location.hash]);
		const onSheetTransitionExited = () => {
			const opener = sheetOpenerRef.current;
			sheetOpenerRef.current = null;
			focusOpener(opener);
		};
		const focusResourcesTrigger = () => resources.triggerRef.current?.focus();

		const openLegal = (dialog: Exclude<LegalDialog, null>) => {
			resources.close();
			// Make the persistent Resources control the modal opener so MUI restores exact focus there.
			focusResourcesTrigger();
			setLegal(dialog);
		};
		const closeLegal = () => setLegal(null);

		/**
		 * Support is ONE entry now (#520). It used to exist twice — this row opening the public feedback
		 * form, and a ProfileMenu row opening the authenticated request flow — so the same intent landed
		 * in two different forms depending on which surface the user found first. The ProfileMenu row is
		 * gone; this row dispatches on auth state instead:
		 *
		 * - anonymous → the public {@link Feedback} contact form, sending the feedback RPC (api#217).
		 *   Unchanged, deliberately: a logged-out visitor has no account for a support request to be
		 *   attributed to, and `/support/request` is authenticated.
		 * - signed in → the {@link SupportRequestDialog} request flow (api#101), which keeps its
		 *   entitlement-driven priority framing and SLA promise.
		 *
		 * Both dialogs are mounted at the top level (below), NOT inside the Resources panel: the hover
		 * menu closes on pointer-leave/outside-click and would unmount a dialog rendered in its subtree.
		 * Each hands us its `open` handle through its trigger render-prop, and the row invokes the right
		 * one after closing the menu, so only the modal remains.
		 *
		 * Focus needs no new machinery here. The opener is the PERSISTENT Resources pill, not a row that
		 * vanishes — unlike the collapsed Branding row of ui#451, whose sheet has to be handed an
		 * explicit return target because its opener unmounts with the popover. Focusing the trigger
		 * before opening, plus each dialog's `onTransitionExited`, returns focus exactly where it started.
		 *
		 * Since ui#1085 there is a SECOND opener — the always-present left-edge feedback tab — and it
		 * routes through this same `openSupportFrom` dispatch on purpose. A tab that opened the public
		 * feedback form unconditionally would put a signed-in user back on the anonymous channel, which
		 * is precisely the defect #520 removed; two entry points are fine, two answers to "where does
		 * this go" are not. What the tab does add is a second RESTORE target, handled below.
		 */
		const openFeedbackRef = useRef<(() => void) | null>(null);
		const openSupportRequestRef = useRef<(() => void) | null>(null);
		const feedbackTabRef = useRef<HTMLButtonElement>(null);
		/**
		 * WHICH control opened the support surface, so focus returns to that one on close (ui#1085).
		 *
		 * There are two openers now — the Resources row and the persistent edge tab — but still only two
		 * dialog instances, and a dialog cannot know who invoked it. Wiring both dialogs to the Resources
		 * pill unconditionally (as they were while it was the only opener) would throw a keyboard user
		 * across the page after closing a dialog they opened from the edge tab.
		 *
		 * An ELEMENT rather than a focus closure, so it goes through the same {@link focusOpener} every
		 * card sheet — the Branding one included — already restores through: that module refuses a detached or
		 * overlay-hidden target and leaves focus where it is instead of dropping it to `<body>`, which a
		 * bare `ref.current?.focus()` cannot do. It also keeps ONE restoration idiom in this file.
		 */
		const supportOpenerRef = useRef<HTMLElement | null>(null);
		/** The ONE dispatch behind both openers — see the auth-state rule documented above (#520). */
		const openSupportFrom = (opener: HTMLElement | null) => {
			supportOpenerRef.current = opener;
			// Both dialogs own their open state, so establish the persistent opener before invoking one.
			focusOpener(opener);
			// Same condition that MOUNTS the request dialog below, so this can never call into a handle
			// left behind by a signed-out session.
			if (loggedIn) {
				openSupportRequestRef.current?.();
				return;
			}
			openFeedbackRef.current?.();
		};
		const onSupportDialogExited = () => {
			const opener = supportOpenerRef.current;
			supportOpenerRef.current = null;
			focusOpener(opener);
		};
		const openSupport = () => {
			resources.close();
			openSupportFrom(resources.triggerRef.current);
		};
		// The row's label follows the ENTITLEMENT, exactly as the retired ProfileMenu row did: a plan
		// carrying `prioritySupport` names what it is entitled to (api#101). The rule itself stays in
		// `support/supportEntitlement`, which is also what `useSupportRequest` asks — so the label a user
		// clicks and the framing the dialog then shows cannot disagree.
		const supportLabel = supportFramingForVisitor(loggedIn, user) === "priority"
			? nav.resourcesMenu.supportPriority
			: nav.resourcesMenu.support;

		const navigateSiteRoute = (path: string) => navigateSiteLink(siteOrigin, path, {
			push: history.push,
			assign: (href) => window.location.assign(href),
		});
		// ui#432: the brand mark is the most-clicked way home, and it is a BUTTON — a client-side push,
		// which the edge never sees. The shared site-route navigator keeps it in-app on a full-site host
		// and leaves the recipient-only shell on a claimed/custom host.
		const onHomeClick = () => navigateSiteRoute("/");
		const closeSheet = () => setSheet(null);
		const onStartTransfer = () => {
			closeSheet();
			if (location.pathname !== "/" || siteOrigin !== null) {
				navigateSiteRoute("/");
			}
		};
		const onResourceLinkClick = (
			event: React.MouseEvent<HTMLAnchorElement>,
			targetResource: ResourceKind | null,
		) => {
			if (targetResource === null) {
				resources.close();
				return;
			}
			if (!isPlainPrimaryClick(event)) {
				return;
			}
			event.preventDefault();
			resources.close();
			focusResourcesTrigger();
			displayedResourceRef.current = targetResource;
			setResource(targetResource);
		};
		const closeResource = () => {
			pendingResourceTargetRef.current = null;
			setResource(null);
		};
		const onResourceTarget = (target: ResourceDialogTarget) => {
			if (target.type === "resource") {
				displayedResourceRef.current = target.resource;
				setResource(target.resource);
				return;
			}
			if (target.type === "navigate") {
				// Preserve native ordering: finish dismissing the modal document before its destination
				// changes. This also keeps the outgoing dialog from briefly covering the next route.
				pendingResourceTargetRef.current = target;
				setResource(null);
				return;
			}
			if (target.type !== "native") {
				pendingResourceTargetRef.current = target;
				setResource(null);
			}
		};
		/**
		 * The Guides row (ui#1555). It is a real anchor at this locale's guides overview page, so every
		 * activation this does NOT swallow — middle-click, new tab, an unhydrated bar — lands on that page;
		 * a plain click opens the picker instead, which is what the row is for.
		 */
		const onGuidesLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
			if (!isPlainPrimaryClick(event)) {
				return;
			}
			event.preventDefault();
			resources.close();
			focusResourcesTrigger();
			setGuidesOpen(true);
		};
		const closeGuides = () => {
			pendingResourceTargetRef.current = null;
			setGuidesOpen(false);
		};
		/**
		 * A link inside a guide that leads out of the picker. It goes through the SAME pending-target ref
		 * every other in-place document uses, so a guide linking to `/pricing` opens the pricing sheet the
		 * way that link does from anywhere else — one replay, not a second one that could drift from it.
		 */
		const onGuidesTarget = (target: ResourceDialogTarget) => {
			pendingResourceTargetRef.current = target;
			setGuidesOpen(false);
		};
		const onResourceTransitionExited = () => {
			const target = pendingResourceTargetRef.current;
			pendingResourceTargetRef.current = null;
			focusResourcesTrigger();
			if (target === null || target.type === "native") {
				return;
			}
			if (target.type === "resource") {
				// Only the guides sheet parks a resource target here: `onResourceTarget` swaps the body of the
				// resource sheet in place instead, because it is already the surface that would open.
				displayedResourceRef.current = target.resource;
				setResource(target.resource);
				return;
			}
			if (target.type === "navigate") {
				// `onTransitionExited` runs before the modal's hidden state has painted. Navigate on the
				// next frame so the old document is observably closed before the route changes.
				window.requestAnimationFrame(() => navigateSiteRoute(target.href));
				return;
			}
			if (target.type === "features") {
				sheetOpenerRef.current = resources.triggerRef.current;
				setFeaturesFocus(target.focus);
				setSheet("features");
				return;
			}
			if (target.type === "pricing") {
				sheetOpenerRef.current = resources.triggerRef.current;
				setSheet("pricing");
				return;
			}
			openLegal("cookie");
		};
		const onMarketingLinkClick = (
			event: React.MouseEvent<HTMLAnchorElement>,
			targetSheet: Exclude<NavSheet, null>,
		) => {
			if (!isPlainPrimaryClick(event)) {
				return;
			}
			event.preventDefault();
			sheetOpenerRef.current = event.currentTarget;
			if (targetSheet === "features") {
				setFeaturesFocus(null);
			}
			setSheet(targetSheet);
		};

		/**
		 * The Resources dropdown — trigger + non-modal hover panel (ui#111) — rendered by BOTH auth
		 * branches (ui#396). Extracting it to ONE element keeps the two bars reaching the identical
		 * policies/guides/cookie panel and the #273/#308 focus/aria hardening in a single place, so the
		 * two copies can never diverge. Only one branch mounts at a time (the `loggedIn` ternary below),
		 * so the single {@link useHoverMenu} state and its `triggerRef` drive exactly one trigger — there
		 * is never a double-render. The trigger + panel share one hover region inside {@link DropdownRoot}
		 * (a DOM descendant), so there is no backdrop and no flicker.
		 *
		 * Language included (ui#812): the two bars render the IDENTICAL panel, so the picker never moves
		 * on login. It used to live in the {@link ProfileMenu} when logged in (ui#117).
		 */
		const resourcesControl = (
			<DropdownRoot
				ref={resources.containerRef}
				className={styles.resourcesRoot}
				{...resources.containerProps}
			>
				<PillButton
					ref={resources.triggerRef}
					type="button"
					disabled={!hasMounted}
					aria-label={nav.resources}
					aria-controls={resourcesPanelId}
					{...resources.triggerProps}
				>
					<PillIcon aria-hidden="true"><ResourcesIcon/></PillIcon>
					<PillLabel>{nav.resources}</PillLabel>
				</PillButton>
				{/* Logged out (the SSG/first render) this keeps the closed route anchors in real HTML for
				    crawlers; the trigger's hydrated aria-expanded controls CSS visibility without mutating
				    the unhydrated panel markup. Logged in the whole bar is client-only, so there is nothing
				    to pre-render — the same panel just mounts after login. */}
				<PanelLayer $align="right" className={styles.resourcesLayer}>
					<Panel id={resourcesPanelId} className={styles.resourcesPanel}>
						<div className={styles.resourceColumns}>
							<div className={styles.resourceGroup} role="group" aria-labelledby={exploreGroupLabelId}>
								<span id={exploreGroupLabelId} className={styles.resourceGroupLabel}>
									{nav.resourcesMenu.explore}
								</span>
								{EXPLORE_RESOURCE_LINKS.map((item) => (
									<PanelItem
										as={RouterLink}
										key={item.to}
										to={item.to}
										onClick={(event) => onResourceLinkClick(event, item.resource)}
									>
										{RESOURCE_LINK_ICONS[item.labelKey]}
										{nav.resourcesMenu[item.labelKey]}
									</PanelItem>
								))}
								{/* Guides (ui#1555) — one row rather than the fourth COLUMN ui#1540 gave the corpus,
								    which the owner did not want. An ANCHOR at this locale's guides overview page, so a
								    middle-click, a crawler and the unhydrated panel all reach a real URL; a plain click
								    opens the picker instead, where every guide loads without leaving the page. Absent
								    for a locale whose corpus ships no overview page — see `content/guideCatalogue`. */}
								{guideNav !== null && (
									<PanelItem
										as={RouterLink}
										to={guideNav.to}
										onClick={onGuidesLinkClick}
									>
										<MenuBookOutlinedIcon aria-hidden="true"/>
										{nav.resourcesMenu.guides}
									</PanelItem>
								)}
								{/* The blog (kitecorp/transferify-ui#3702) is a static site of its own, served at /blog
								    from its own bucket (kitecorp/transferify#592), so the row loads it as a new document:
								    a router push would match the catch-all route, which sends the reader home. Absent
								    where the catalog has no word for it (German). */}
								{nav.resourcesMenu.blog !== null && (
									<PanelItem
										as={RouterLink}
										to={BLOG_INDEX_PATH}
										reloadDocument
										onClick={(event: React.MouseEvent<HTMLAnchorElement>) => onResourceLinkClick(event, null)}
									>
										<NewspaperOutlinedIcon aria-hidden="true"/>
										{nav.resourcesMenu.blog}
									</PanelItem>
								)}
							</div>
							{/* Advertising is its own column in BOTH auth states. The entry used to be a
							    ProfileMenu row, reachable only after signing in — an offer aimed at people who
							    have never used the product cannot live behind a login. It points at the
							    PRESENTATION page, which is what hands off to the booking flow. */}
							<div className={styles.resourceGroup} role="group" aria-labelledby={advertisingGroupLabelId}>
								<span id={advertisingGroupLabelId} className={styles.resourceGroupLabel}>
									{nav.resourcesMenu.advertising}
								</span>
								{ADVERTISING_RESOURCE_LINKS.map((item) => (
									<PanelItem
										as={RouterLink}
										key={item.to}
										to={item.to}
										onClick={(event) => onResourceLinkClick(event, item.resource)}
									>
										{RESOURCE_LINK_ICONS[item.labelKey]}
										{nav.resourcesMenu[item.labelKey]}
									</PanelItem>
								))}
							</div>
							<div className={styles.resourceGroup} role="group" aria-labelledby={supportLegalGroupLabelId}>
								<span id={supportLegalGroupLabelId} className={styles.resourceGroupLabel}>
									{nav.resourcesMenu.supportLegal}
								</span>
								<PanelItem type="button" onClick={openSupport}>
									<SupportAgentOutlinedIcon aria-hidden="true"/>
									{supportLabel}
								</PanelItem>
								{LEGAL_RESOURCE_LINKS.map((item) => (
									<PanelItem
										as={RouterLink}
										key={item.to}
										to={item.to}
										onClick={(event) => onResourceLinkClick(event, item.resource)}
									>
										{RESOURCE_LINK_ICONS[item.labelKey]}
										{nav.resourcesMenu[item.labelKey]}
									</PanelItem>
								))}
								<PanelItem type="button" onClick={() => openLegal("cookie")}>
									<CookieOutlinedIcon aria-hidden="true"/>
									{nav.resourcesMenu.cookie}
								</PanelItem>
								<PanelItem type="button" onClick={() => openLegal("cookieSettings")}>
									<TuneOutlinedIcon aria-hidden="true"/>
									{nav.resourcesMenu.cookieSettings}
								</PanelItem>
								{/* In BOTH auth states (ui#812) — it lived in the ProfileMenu when logged in
								    (ui#117), which meant the picker moved on login. One home, either state. */}
								<LanguageSubmenuItem
									icon={<TranslateOutlinedIcon aria-hidden="true"/>}
									onSelected={resources.close}
								/>
							</div>
						</div>
					</Panel>
				</PanelLayer>
			</DropdownRoot>
		);

		return (
			<>
				<Topbar ref={ref} {...props}>
					<Brand type="button" onClick={onHomeClick} aria-label={nav.homeAria}>
						<LogoMark aria-hidden="true"/>
						<Wordmark>{brand.name}</Wordmark>
					</Brand>
					{loggedIn ? (
						/* The marker is what lets the feature sheet call this bar its OWN controls rather
						   than the page behind it (ui#1534) — see `nav/navPillBar`. Stamped on both auth
						   states so the two bars cannot drift into disagreeing about what the pill bar is,
						   even though only the logged-in one currently renders over a feature sheet. */
						<NavLinks
							aria-label={nav.primaryAria}
							className={styles.navPill}
							{...{[NAV_PILL_BAR_ATTR]: ""}}
						>
							{/* The pill stays at four primary destinations. Contacts and Reminders additionally carry
							    a `$collapse` marker: below their thresholds they leave the bar and the ProfileMenu
							    renders them instead (ui#451), from the same `nav.destinations` labels — so no width
							    shows a destination twice. Account and Personalization are the two rows this map skips;
							    {@link PILL_RENDERED_ELSEWHERE} says why each is excluded. */}
							{destinations
								.filter((destination) => !PILL_RENDERED_ELSEWHERE.has(destination.path))
								.map((destination) => (
									<DestinationLink
										key={destination.path}
										// ui#432: the authenticated app is denied on a claimed host too, so these
										// leave for the brand origin there. `preloadRoute` and `aria-current` below
										// keep using the RAW path — they address this build's route table, not a URL.
										to={destination.path}
										// A delivered dismissing click must consume `/personalization`, not push on top
										// of it. The queued close then sees this destination and leaves history alone.
										replace={brandOpen}
										$collapse={collapsibleNavItem(destination.key)}
										aria-label={destination.label}
										aria-current={isCurrentDestination(location.pathname, destination.path) ? "page" : undefined}
										aria-disabled={destination.disabled ? "true" : undefined}
										tabIndex={destination.disabled ? -1 : undefined}
										// Prefetch the target route's chunk on pointer/focus/touch intent (#146) — a beat
										// before the click, so the open is instant. `preloadRoute` is idempotent and a
										// no-op for non-private/disabled paths.
										onPointerEnter={() => {
											if (!destination.disabled) preloadRoute(destination.path);
										}}
										onFocus={() => {
											if (!destination.disabled) preloadRoute(destination.path);
										}}
										onTouchStart={() => {
											if (!destination.disabled) preloadRoute(destination.path);
										}}
										onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
											if (destination.disabled) event.preventDefault();
										}}
									>
										<PillIcon aria-hidden="true">{DESTINATION_ICONS[destination.key]}</PillIcon>
										<PillLabel>{destination.label}</PillLabel>
									</DestinationLink>
								))}
							{/* Personalization (ui#450, given a route in ui#1292). It sits with the app items, before
							    the Resources dropdown, and stays enabled while an account awaits activation for the
							    same reason /account does (#12): it edits account settings, and its own gate —
							    `useAccountBrand` — is what decides what the sender may do inside.

							    It IS an `appDestinations` entry now. ui#450 kept it out of that table because the
							    sheet lived at no URL; it lives at `/personalization` since ui#1292, so the route
							    model carries it and the command palette offers it like any other destination. What
							    it is NOT is a `DestinationLink` in the map above, which is why it is still written
							    out here: this control is a TOGGLE BUTTON (see below), not a router link, so
							    `NavBar` and `ProfileMenu` both render it themselves and exclude it from the mapped
							    rows — {@link PILL_RENDERED_ELSEWHERE} says so at the filter.

							    Its `$collapse` range is a band (ui#452), so ≤640px this button is in the bar as a
							    44px icon square. `aria-label` is what a screen reader announces once {@link
							    PillLabel} is hidden, and it is the same catalog string the label renders, so the
							    spoken and the visible name cannot drift apart.

							    It TOGGLES, and it says so (ui#1243). Both halves are one fix: the handler used to
							    call `openBrandSheet` unconditionally, so a click while the sheet was open set
							    `true` over `true` and React bailed out — a click that did literally nothing. Since
							    nothing on the bar drew the open state either, that no-op was indistinguishable from
							    the sheet closing and reopening, which is how it was reported.

							    Since ui#1247 the `brandOpen` branch is a DEFENSIVE REMNANT rather than the live
							    path, and saying so is the point: the sheet light-dismisses on a click outside it and
							    swallows that click at document capture, so this handler no longer runs at all while
							    the sheet is open — by mouse or by keyboard, since Enter and Space dispatch a real
							    bubbling click too. ui#1278 narrowed the swallow but not for this control: what now
							    travels on the dismissing click is a LINK in the nav landmark, and this is a
							    `<button>`, so it is still eaten exactly as the theme toggle beside it is. It is kept
							    because it is what closes the sheet if the dismissal is ever turned off for this
							    caller, and because deleting it would restore ui#1243's dead click in that state.
							    `aria-expanded` is
							    what fixes the second half: it announces the state to a screen reader AND is what
							    {@link SheetPillButton} keys its active treatment on, so the lit pill and the open
							    sheet are the same fact rather than two that can disagree. Since ui#1292 that one
							    fact is the ROUTE, so a deep link lights the pill without anything on the bar having
							    been clicked. It remains `aria-expanded` rather than the `aria-current` the router
							    pills get: this control opens and closes a surface, and a toggle button's state is
							    what a screen-reader user needs to hear before pressing it again. */}
							<SheetPillButton
								type="button"
								$collapse="branding"
								aria-label={nav.branding}
								aria-expanded={brandOpen}
								onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
									if (brandOpen) {
										closeBrandSheet();
										return;
									}
									openBrandSheet(event.currentTarget);
								}}
							>
								<PillIcon aria-hidden="true"><BrandingIcon size={PILL_GLYPH_SIZE}/></PillIcon>
								<PillLabel>{nav.branding}</PillLabel>
							</SheetPillButton>
							{/* Resources joins the logged-in bar too (ui#396) — the SAME dropdown the public bar
							    shows, Language included (ui#812), in the logged-out placement pattern: after the
							    destinations, before the separator/toggle/avatar. It is the same icon-only-≤640px
							    pill as the destinations, so the row stays one line. */}
							{resourcesControl}
							<NavSeparator aria-hidden="true"/>
							<ToggleSlot>
								<DarkThemeToggle/>
							</ToggleSlot>
							{/* The ProfileMenu holds the pill items that collapse out of the bar on a narrow
							    viewport (ui#451); Language is NOT among them anymore — it stays in Resources for
							    both auth states (ui#812). Personalization has a route since ui#1292 but the row still
							    delegates here rather than pushing it itself (see `ProfileMenu`'s prop doc — the row
							    unmounts, so only the bar can name a focus-return target), so
							    the menu delegates back here instead of mounting a SECOND {@link BrandSheet} — one
							    sheet, one piece of open state, either opener. */}
							<ProfileMenu
								onOpenBranding={openBrandSheet}
								onOpenAnalytics={openAnalyticsSheet}
								onOpenBilling={openBillingSheet}
								onTriggerRef={(node) => {
									profileTriggerRef.current = node;
								}}/>
						</NavLinks>
					) : (
						<NavLinks
							aria-label={nav.primaryAria}
							className={styles.navPill}
							{...{[NAV_PILL_BAR_ATTR]: ""}}
						>
							{/* Home, first and permanent (ui#780). It used to be reachable only from the Resources
							    menu, which put the product's own front door two gestures deep on every public page.

							    A plain link with NO onClick, unlike its two neighbours: Features and Pricing
							    intercept the click to open a dialog, whereas this one genuinely navigates.
							    `RouterLink` is the site-link seam (`nav/SiteLink`), which is what makes it correct
							    off the root tree: on a claimed host it resolves to an absolute brand-origin URL, the
							    same reason the brand mark's own handler leaves (ui#432), and under `/ro`/`/de` on the
							    main domain it stays inside that tree (ui#2498). On every host that serves "/" itself
							    it stays an in-app router push.

							    It carries the pill's icon + label pair, so ≤640px it becomes the 44px icon-only
							    square every other pill item becomes (ui#452), with `aria-label` — the same catalog
							    string the label renders — carrying the name once {@link PillLabel} is hidden. Its
							    label comes from `nav.destinations.home`, the SAME key the logged-in pill uses, so
							    the two bars cannot come to call the front door different things. */}
							<MarketingAnchor
								to="/"
								aria-label={nav.destinations.home}
								// Tree-relative, so the home pill reads as current on `/ro` and `/de` as well as at
								// the root — the same page, addressed two ways (ui#2498).
								aria-current={treeRelativePath(location.pathname) === "/" ? "page" : undefined}
							>
								<PillIcon aria-hidden="true">{DESTINATION_ICONS.home}</PillIcon>
								<PillLabel>{nav.destinations.home}</PillLabel>
							</MarketingAnchor>
							<MarketingAnchor
								to={"/features"}
								aria-label={nav.features}
								onClick={(event) => onMarketingLinkClick(event, "features")}
							>
								<PillIcon aria-hidden="true"><FeaturesIcon/></PillIcon>
								<PillLabel>{nav.features}</PillLabel>
							</MarketingAnchor>
							<MarketingAnchor
								to={"/pricing"}
								aria-label={nav.pricing}
								onClick={(event) => onMarketingLinkClick(event, "pricing")}
							>
								<PillIcon aria-hidden="true"><PricingIcon/></PillIcon>
								<PillLabel>{nav.pricing}</PillLabel>
							</MarketingAnchor>
							{/* The SAME Resources dropdown the logged-in bar renders (ui#396); see its definition
							    above. Logged out it also keeps the Language row and stays in SSG HTML for crawlers. */}
							{resourcesControl}
							<NavSeparator aria-hidden="true"/>
							<LoginButton type="button" aria-label={nav.login} title={nav.login} onClick={() => loginModal.open()}>
							<LoginGlyph aria-hidden="true"><AccountIcon size={20}/></LoginGlyph>
							<LoginLabel>{nav.login}</LoginLabel>
						</LoginButton>
						</NavLinks>
					)}
				</Topbar>
				<ResourceDialog
					resource={resource ?? displayedResourceRef.current}
					open={resource !== null}
					onClose={closeResource}
					onTarget={onResourceTarget}
					onTransitionExited={onResourceTransitionExited}
				/>
				{/* The Guides picker (ui#1555), mounted only where the locale has guides to pick from — the
				    same condition that renders its Explore row, so the row and the sheet cannot disagree. It
				    shares `onResourceTransitionExited`, and therefore the one pending-target replay. */}
				{guideNav !== null && (
					<GuidesDialog
						open={guidesOpen}
						choices={guideNav.choices}
						onClose={closeGuides}
						onTarget={onGuidesTarget}
						onTransitionExited={onResourceTransitionExited}
					/>
				)}
				{/* This exit callback is the sole owner; ModalSheet disables its MUI trap restore. */}
				<HowItWorksDialog
					open={sheet === "features"}
					onClose={closeSheet}
					onTransitionExited={onSheetTransitionExited}
					focusTarget={featuresFocus}
					startTransferPath="/"
					onStartTransfer={onStartTransfer}
				/>
				{/* This exit callback is the sole owner; ModalSheet disables its MUI trap restore. */}
				<PricingDialog
					open={sheet === "pricing"}
					onClose={closeSheet}
					onUpgrade={onUpgrade}
					onTransitionExited={onSheetTransitionExited}
				/>
				{/* The existing branding editor on the shared NON-MODAL card sheet (was a right side sheet
				    in ui#457 and a centred dialog in ui#450). Mounted here with the other bar-opened
				    overlays; a closed card sheet renders nothing, so it costs no request until the sender
				    opens it — and while it is open THIS bar stays reachable above it, which is the whole
				    reason the card replaced the modal. */}
				{/* A route-driven open has no captured opener: the command palette, a deep link or a typed URL
				    can all enter `/personalization` without clicking this bar. Resolve the durable avatar only
				    when the sheet returns focus, because its ref commits after the first authenticated render.
				    The avatar rather than this surface's own pill: the ui#452 band hides that pill with
				    `display: none` at 641–879px while leaving it mounted, and a hidden element accepts `.focus()`
				    by doing nothing. A real in-app opener stays first choice through `returnFocus`. */}
				<BrandSheet
					open={brandOpen}
					onClose={closeBrandSheet}
					returnFocus={brandOpenerRef.current}
					returnFocusFallback={resolveProfileTrigger}
					onExited={onBrandSheetExited}
				/>
				{/* The account's analytics (ui#1522). Signed-in only, and nothing is even fetched until the
				    dropdown row is picked — see the lazy chunk's note above for why that is required
				    rather than merely tidy. */}
				{loggedIn && analyticsMounted && (
					<Suspense fallback={null}>
						{/* `returnFocus` + `returnFocusFallback` exactly as the branding sheet above: the
						    ProfileMenu row that raised this is gone by the time it closes, and the avatar is
						    the one control the bar keeps mounted AND visible at every width. */}
						<AnalyticsSheet
							open={analyticsOpen}
							onClose={closeAnalyticsSheet}
							returnFocus={analyticsOpenerRef.current}
							returnFocusFallback={resolveProfileTrigger}
							onExited={onAnalyticsSheetExited}
						/>
					</Suspense>
				)}
				{/* The account's billing (ui#1791). Signed-in only, and its chunk is not fetched until the
				    dropdown row is picked — the same treatment the analytics sheet above gets. */}
				{loggedIn && billingMounted && (
					<Suspense fallback={null}>
						{/* `returnFocus` + `returnFocusFallback` exactly as the two sheets above: the
						    ProfileMenu row that raised this is gone by the time it closes, and the avatar is
						    the one control the bar keeps mounted AND visible at every width. */}
						<BillingSheet
							open={billingOpen}
							onClose={closeBillingSheet}
							returnFocus={billingOpenerRef.current}
							returnFocusFallback={resolveProfileTrigger}
							onExited={onBillingSheetExited}
						/>
					</Suspense>
				)}
				<CompactUpgradeDialog checkout={checkout} onClose={closeCheckout}/>
				<PlanChangeDialog {...planChange}/>
				<PolicyDialog kind="cookie" open={legal === "cookie"} onClose={closeLegal}
							  onTransitionExited={focusResourcesTrigger}/>
				<CookieSettingsDialog open={legal === "cookieSettings"} onClose={closeLegal}
								  onTransitionExited={focusResourcesTrigger}/>
				{/* The second entry point (ui#1085): a persistent left-edge tab, desktop only, dispatching
				    through the SAME auth-state rule as the Resources row above. Mounted here so it is
				    present on every route, and `position: fixed` so this DOM position is not its
				    on-screen one. */}
				<FeedbackEdgeTab ref={feedbackTabRef} onOpen={() => openSupportFrom(feedbackTabRef.current)}/>
				{/* The two dialogs the ONE Resources "Support" row dispatches to (#520) — mounted here
				    (top level, not inside the hover panel) so they survive the menu closing. Each
				    trigger render-prop hands us its `open` handle and renders no visible control.
				    `onTransitionExited` reads the ref rather than closing over one opener, so focus goes
				    back to whichever control actually opened the dialog. */}
				<Feedback onTransitionExited={onSupportDialogExited} trigger={(open) => {
					openFeedbackRef.current = open;
					return null;
				}}/>
				{/* Signed-in only, and `useIsLoggedIn` is false during the pre-render AND the first client
				    render — so the authenticated flow never reaches the SSG output of a public page. */}
				{loggedIn && (
					<SupportRequestDialog onTransitionExited={onSupportDialogExited} trigger={(open) => {
						openSupportRequestRef.current = open;
						return null;
					}}/>
				)}
			</>
		);
	},
);

export default NavBar;
